// 출력물 미리보기 전체화면 오버레이 (거래명세서·세금계산서 공용).
//
// body 직속에 portal로 올린다. 인쇄 스타일이 `body > *:not(.inv-print-root)`
// 한 줄로 앱 크롬 전체를 숨길 수 있는 것이 이 배치 덕분이다.
// 오버레이가 뷰포트를 완전히 덮으므로 열려 있는 동안 탭 전환이 불가능하다.
// (클래스명의 inv- 접두는 거래명세서에서 시작된 이름이며 지금은 공용이다)

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Printer } from 'lucide-react';

// 용지 설정. @page는 선택자가 없어 CSS에서 조건부로 쓸 수 없다.
// print-invoice.css는 앱 전체에 로드되므로 거기 두면 다른 화면 인쇄까지
// A4·여백 0이 된다. 오버레이가 열려 있는 동안만 주입한다.
const ORIENTATION_LABEL = { landscape: '가로', portrait: '세로' } as const;

interface PrintPreviewOverlayProps {
  title: string;
  onClose: () => void;
  /** 용지 방향. 거래명세서는 세로 1면, 세금계산서는 가로(기본) */
  orientation?: 'landscape' | 'portrait';
  /** 툴바 가운데에 끼우는 편집 요소. 툴바는 인쇄 시 숨겨지므로 출력물에는 나오지 않는다 */
  toolbarExtra?: React.ReactNode;
  /** 인쇄 버튼 왼쪽에 끼우는 동작 버튼 (거래명세서의 엑셀 내려받기) */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

export function PrintPreviewOverlay({
  title,
  onClose,
  orientation = 'landscape',
  toolbarExtra,
  actions,
  children,
}: PrintPreviewOverlayProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `@page { size: A4 ${orientation}; margin: 0; }`;
    document.head.appendChild(style);
    return () => style.remove();
  }, [orientation]);

  return createPortal(
    <div className='inv-print-root'>
      <div className='inv-toolbar'>
        <span>{title}</span>
        {toolbarExtra}
        <span className='inv-toolbar-hint'>
          인쇄 설정: 용지 A4 {ORIENTATION_LABEL[orientation]} · 여백 없음 · 배율 100% · 배경 그래픽 켜기
        </span>
        <span>
          {actions}
          <button type='button' onClick={() => window.print()}>
            <Printer size={14} />
            인쇄
          </button>
          <button type='button' onClick={onClose}>
            닫기 (ESC)
          </button>
        </span>
      </div>
      {children}
    </div>,
    document.body
  );
}
